import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { medicationService } from '../services/apiServices';
import { Medication } from '../types';

interface MedicationTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  medication: Medication | null;
  transactionType: 'purchase' | 'usage' | 'adjustment' | 'disposal';
  procurementId?: number;
}

const MedicationTransactionDialog: React.FC<MedicationTransactionDialogProps> = ({
  open,
  onClose,
  medication,
  transactionType,
  procurementId,
}) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    quantity: 0,
    unit_cost: 0,
    reason: '',
    notes: '',
    transaction_date: new Date().toISOString().split('T')[0],
  });

  const purchaseMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      medicationService.recordPurchase(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      handleClose();
    },
  });

  const usageMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      medicationService.recordUsage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      handleClose();
    },
  });

  const adjustmentMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      medicationService.recordAdjustment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      handleClose();
    },
  });

  const disposalMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      medicationService.recordDisposal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      handleClose();
    },
  });

  const handleChange = (field: string) => (e: any) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!medication) return;

    try {
      const dataToSend: any = {
        quantity: parseFloat(formData.quantity.toString()),
        transaction_date: formData.transaction_date,
      };

      if (transactionType === 'purchase') {
        if (formData.unit_cost > 0) {
          dataToSend.unit_cost = parseFloat(formData.unit_cost.toString());
        }
        if (formData.notes) dataToSend.notes = formData.notes;
        await purchaseMutation.mutateAsync({ id: medication.id, data: dataToSend });
      } else if (transactionType === 'usage') {
        dataToSend.bird_procurement_id = procurementId;
        if (formData.reason) dataToSend.reason = formData.reason;
        if (formData.notes) dataToSend.notes = formData.notes;
        await usageMutation.mutateAsync({ id: medication.id, data: dataToSend });
      } else if (transactionType === 'adjustment') {
        dataToSend.reason = formData.reason;
        if (formData.notes) dataToSend.notes = formData.notes;
        await adjustmentMutation.mutateAsync({ id: medication.id, data: dataToSend });
      } else if (transactionType === 'disposal') {
        dataToSend.reason = formData.reason;
        if (formData.notes) dataToSend.notes = formData.notes;
        await disposalMutation.mutateAsync({ id: medication.id, data: dataToSend });
      }
    } catch (error: any) {
      console.error('Failed to record transaction:', error);
      alert(error?.response?.data?.message || 'Failed to record transaction');
    }
  };

  const handleClose = () => {
    setFormData({
      quantity: 0,
      unit_cost: 0,
      reason: '',
      notes: '',
      transaction_date: new Date().toISOString().split('T')[0],
    });
    onClose();
  };

  const getDialogTitle = () => {
    switch (transactionType) {
      case 'purchase':
        return 'Record Purchase';
      case 'usage':
        return 'Record Usage';
      case 'adjustment':
        return 'Stock Adjustment';
      case 'disposal':
        return 'Record Disposal';
      default:
        return 'Transaction';
    }
  };

  const isPending =
    purchaseMutation.isPending ||
    usageMutation.isPending ||
    adjustmentMutation.isPending ||
    disposalMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {medication && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Medication: <strong>{medication.name}</strong> (Current stock:{' '}
              {medication.quantity_in_stock} {medication.unit})
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                type="number"
                label={transactionType === 'adjustment' ? 'Quantity (+/-)' : 'Quantity'}
                value={formData.quantity}
                onChange={handleChange('quantity')}
                inputProps={{
                  step: 0.01,
                  ...(transactionType === 'adjustment' ? {} : { min: 0.01 }),
                }}
                helperText={
                  transactionType === 'adjustment'
                    ? 'Use positive for increase, negative for decrease'
                    : undefined
                }
              />
            </Grid>

            {transactionType === 'purchase' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Unit Cost (GHS)"
                  value={formData.unit_cost}
                  onChange={handleChange('unit_cost')}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Leave empty to use current unit cost"
                />
              </Grid>
            )}

            {(transactionType === 'usage' ||
              transactionType === 'adjustment' ||
              transactionType === 'disposal') && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  required={transactionType !== 'usage'}
                  label="Reason"
                  value={formData.reason}
                  onChange={handleChange('reason')}
                  multiline
                  rows={2}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="date"
                label="Transaction Date"
                value={formData.transaction_date}
                onChange={handleChange('transaction_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            Record
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MedicationTransactionDialog;
